import {
  useAddFriend,
  useFriendsDailyMysteryCityPerformance,
  useRemoveFriend,
} from '@src/hooks/useFriends';
import { Button, HStack, Skeleton, Text, VStack } from 'ui_2';
import mystery_city from '@assets/mystery_city_daily_game_header.png';
import part_img from '@assets/part.png';
import { useReferralCode } from '@src/hooks/useReferrals';
import { FriendGamePerformance } from 'jigsaw';
import { useCallback, useMemo } from 'react';
import { useDappState } from '@src/hooks/useDappState';
import { getDeeplinkBrowserUrl } from '@puzzlehq/frontend-utils';
import dayjs from 'dayjs';
import { BiCheck, BiX } from 'react-icons/bi';

const PerformanceRow = ({
  friend,
  ix,
}: {
  friend: FriendGamePerformance;
  ix: number;
}) => {
  const { isPuzzleWalletWebview } = useDappState();
  const { data } = useReferralCode();
  const isUser =
    !!friend.friendReferralCode &&
    friend.friendReferralCode === data?.referralCode;
  const hasPlayed = friend.part !== null;

  const {
    addFriend,
    isLoading: isLoadingAddFriend,
    isSuccess: isSuccessAddFriend,
  } = useAddFriend(friend.friendReferralCode);
  const {
    removeFriend,
    isLoading: isLoadingRemoveFriend,
    isSuccess: isSuccessRemoveFriend,
  } = useRemoveFriend(friend.friendReferralCode);

  const onClickPlay = useCallback(() => {
    const baseUrl = 'https://mysterycity.puzzlethebar.com';
    if (isPuzzleWalletWebview) {
      const url = getDeeplinkBrowserUrl(baseUrl, 'rootNavigationFlow');
      window.open(url, '_blank');
    } else {
      window.open(baseUrl, '_blank');
    }
  }, [isPuzzleWalletWebview]);

  const onClickNudge = () => {
    navigator.share({
      title: 'Mystery City Check-in',
      text: `Just finished my daily Mystery City game ${
        friend.friendReferralCode ? friend.friendReferralCode : 'friend'
      }! Your turn!`,
      url: 'https://app.puzzle.online',
    });
  };

  return (
    <HStack className='h-6 w-full items-center justify-between gap-4'>
      <HStack className='items-baseline gap-4'>
        <Text font='medium-sm' color='gray-500'>
          {ix + 1}
        </Text>
        <HStack className='items-baseline gap-2'>
          <Text font='sm'>{friend.friendReferralCode}</Text>
          {isUser && (
            <Text font='xs' color='gray-500'>
              (you)
            </Text>
          )}
        </HStack>
      </HStack>

      {friend.status === 'friend' && friend.part !== null && (
        <HStack className='items-center gap-2'>
          <img src={part_img} className='-mt-[2px] h-4 w-4' />
          <Text>{friend.part}</Text>
        </HStack>
      )}
      {friend.status === 'friend' && !isUser && !hasPlayed && (
        <Button
          variant='gray'
          onClick={onClickNudge}
          size='sm'
          className='px-3'
        >
          <Text font='xs'>👋 Nudge!</Text>
        </Button>
      )}
      {friend.status === 'friend' && isUser && !hasPlayed && (
        <Button variant='mysteryCitySecondary' size='sm' onClick={onClickPlay}>
          Play!
        </Button>
      )}
      {friend.status === 'sent' && !isUser && (
        <HStack className='items-center gap-2'>
          <Text font='xs'>Requested</Text>
          <Button
            onClick={removeFriend}
            variant='gray'
            size='sm'
            className='px-3'
            disabled={isLoadingAddFriend || isLoadingRemoveFriend}
          >
            <Text font='xs'>Delete</Text>
          </Button>
        </HStack>
      )}
      {friend.status === 'pending' && !isUser && (
        <HStack className='gap-2' key={friend.walletAddress}>
          <Button
            onClick={addFriend}
            variant='black'
            size='sm'
            className='px-3'
            disabled={isLoadingAddFriend || isLoadingRemoveFriend}
          >
            <Text font='xs' color='white'>
              Confirm
            </Text>
          </Button>
          <Button
            onClick={removeFriend}
            variant='gray'
            size='sm'
            className='px-3'
            key={friend.walletAddress}
            disabled={isLoadingAddFriend || isLoadingRemoveFriend}
          >
            <Text font='xs'>Delete</Text>
          </Button>
        </HStack>
      )}
    </HStack>
  );
};

export const MysteryCityFriendPerformance = () => {
  const { data, isLoading, fetchStatus } =
    useFriendsDailyMysteryCityPerformance();

  const dataToDisplay = useMemo(() => {
    return data?.filter((friend) => !!friend.friendReferralCode);
  }, [data]);

  const today = useMemo(() => new Date(), []);

  const isActuallyLoading = isLoading && fetchStatus !== 'idle';

  return (
    <VStack className='w-full max-w-[500px] overflow-clip rounded-t-[10px] shadow-sm'>
      <img src={mystery_city} />
      <VStack className='items-center gap-4 rounded-b-[10px] border border-gray-100 p-4'>
        <Text font='sm'>Today, {dayjs(today).format('MMMM D')}</Text>
        <VStack className='w-full gap-3'>
          {isActuallyLoading &&
            Array.from({ length: 3 }).map((_, ix) => (
              <Skeleton className='h-[50px] w-full' delay={ix} key={ix} />
            ))}
          {dataToDisplay?.map((friend, ix) => (
            <PerformanceRow friend={friend} key={ix} ix={ix} />
          ))}
          {!isActuallyLoading &&
            dataToDisplay !== undefined &&
            dataToDisplay.length === 0 && (
              <Text font='sm' color='gray-500'>
                No activity today
              </Text>
            )}
        </VStack>
      </VStack>
    </VStack>
  );
};
